import { ConvertibleStatus, InstrumentType } from '@/types/captable';
import type { ConvertibleInstrument } from '@/types/captable';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

interface StatusConfigEntry {
  label: string;
  variant: BadgeVariant;
}

export const CONVERTIBLE_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  [ConvertibleStatus.DRAFT]: { label: 'Draft', variant: 'outline' },
  [ConvertibleStatus.RUNNING]: { label: 'Running', variant: 'default' },
  [ConvertibleStatus.CONVERTED]: { label: 'Converted', variant: 'secondary' },
  [ConvertibleStatus.TERMINATED]: {
    label: 'Terminated',
    variant: 'destructive',
  },
  [ConvertibleStatus.REPAID]: { label: 'Repaid', variant: 'outline' },
  [ConvertibleStatus.EXERCISED]: { label: 'Exercised', variant: 'secondary' },
};

const CONVERSION_PENDING_BADGE: StatusConfigEntry = {
  label: 'Conversion pending',
  variant: 'outline',
};

const REPAYMENT_PENDING_BADGE: StatusConfigEntry = {
  label: 'Repayment pending',
  variant: 'outline',
};

/**
 * Pick the badge for a convertible instrument.
 *
 * A RUNNING instrument with a pending conversion/repayment transaction is
 * displayed as 'Conversion pending' / 'Repayment pending'. The status flips
 * to CONVERTED / REPAID once the captable reconciliation pipeline approves
 * the paired ledger transaction.
 */
export function getConvertibleStatusBadge(
  instrument: Pick<
    ConvertibleInstrument,
    'status' | 'conversion_transaction_id' | 'repayment_transaction_id'
  >
): StatusConfigEntry {
  if (instrument.status === ConvertibleStatus.RUNNING) {
    if (instrument.conversion_transaction_id) {
      return CONVERSION_PENDING_BADGE;
    }
    if (instrument.repayment_transaction_id) {
      return REPAYMENT_PENDING_BADGE;
    }
  }
  return (
    CONVERTIBLE_STATUS_CONFIG[instrument.status] ||
    CONVERTIBLE_STATUS_CONFIG.running
  );
}

/**
 * Whether a conversion, repayment or exercise can be recorded. A draft SAFE or
 * note takes one ahead of its issuance's approval; a warrant waits until running.
 */
export function acceptsResolution(
  instrument: Pick<ConvertibleInstrument, 'status' | 'instrument_type'>
): boolean {
  if (instrument.status === ConvertibleStatus.RUNNING) return true;
  return (
    instrument.status === ConvertibleStatus.DRAFT &&
    instrument.instrument_type !== InstrumentType.WARRANT
  );
}
